import { SubscriptionType } from '@sc/graphql';
import {
  Button,
  ESSENTIALS_FROM_PRICE,
  essentialsList,
  Price,
  scaleList,
} from '@sc/shared-ui';
import { ComponentType, ReactElement, SVGProps, useState } from 'react';
import classNames from 'classnames';
import { UpgradeCheckoutModal } from '../modals/UpgradeCheckoutModal';
import {
  ArrowRightIcon,
  CheckCircleIcon,
  CircleStackIcon,
} from '@heroicons/react/24/outline';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

interface Props {
  upgradePrompt: string;
  subscriptionType: SubscriptionType;
  Icon?: IconComponent | undefined;
  modal?: boolean | undefined;
  onSuccess?: (() => void) | undefined;
}

type PlanCopy = {
  name: string;
  price: string | null;
  bullets: string[];
};

const PLAN_COPY: Record<SubscriptionType, PlanCopy> = {
  [SubscriptionType.essentials]: {
    name: 'Essentials',
    price: `From ${ESSENTIALS_FROM_PRICE}`,
    bullets: essentialsList,
  },
  [SubscriptionType.enterprise]: {
    name: 'Scale',
    price: null,
    bullets: scaleList,
  },
};

export const BillingLite = ({
  upgradePrompt,
  modal,
  subscriptionType,
  Icon = CircleStackIcon,
  onSuccess,
}: Props): ReactElement => {
  const plan = PLAN_COPY[subscriptionType];
  const listItems = plan.bullets;
  const [showCheckout, setShowCheckout] = useState(false);

  const isSelfServe = subscriptionType !== SubscriptionType.enterprise;

  return (
    <div
      className={classNames(
        'flex flex-col items-start text-left px-8 pb-8',
        modal ? 'pt-0' : 'pt-10'
      )}
    >
      <div className="self-stretch flex flex-col items-center text-center">
        <div className="mb-6 p-6 rounded-lg bg-primary-500/10">
          <Icon className="size-11 text-primary-400" />
        </div>

        <h1 className="mb-2 font-grotesque font-bold text-h2 text-white">
          Upgrade to {plan.name}
        </h1>

        <p className="mb-6 text-text-low-priority max-w-sm">{upgradePrompt}</p>
      </div>

      {plan.price !== null ? (
        <Price amount={plan.price} />
      ) : (
        <Price amount="Custom pricing" suffix="" />
      )}

      <ul
        className="mb-8 flex flex-col gap-4 items-start self-start text-left"
        role="list"
      >
        {listItems.map((text) => (
          <li className="flex gap-2 items-start" key={text}>
            <CheckCircleIcon
              className="mt-1.5 w-5.5 h-5.5 text-grey-400 shrink-0"
              aria-hidden="true"
            />
            <p className="font-grotesque text-xl leading-7.5 font-semibold text-white">
              {text}
            </p>
          </li>
        ))}
      </ul>

      {isSelfServe ? (
        <>
          <Button
            button={{
              text: `Upgrade to ${plan.name}`,
              size: 'xl',
              IconEnd: ArrowRightIcon,
              onClick: () => setShowCheckout(true),
            }}
          />
          <UpgradeCheckoutModal
            open={showCheckout}
            priceStep={0}
            onSuccess={onSuccess}
            onClose={() => setShowCheckout(false)}
          />
        </>
      ) : (
        <Button
          button={{
            text: `Upgrade to ${plan.name}`,
            size: 'xl',
            IconEnd: ArrowRightIcon,
          }}
          link={{ href: 'https://starkchat.com/contact', target: '_blank' }}
        />
      )}
    </div>
  );
};
